import React, { useState, useEffect } from 'react';
import { fileAPI } from '../lib/api';

const FilePreviewModal = ({ file, isOpen, onClose }) => {
	const [previewUrl, setPreviewUrl] = useState(null);
	const [textContent, setTextContent] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		if (isOpen && file) {
			loadPreview();
		} else {
			setPreviewUrl(null);
			setTextContent(null);
			setError(null);
		}
	}, [isOpen, file]);

	const getFileType = () => {
		if (!file || !file.contentType) return 'unknown';
		const contentType = file.contentType.toLowerCase();

		if (contentType.includes('pdf')) return 'pdf';
		if (contentType.includes('image')) return 'image';
		if (contentType.includes('text') || contentType.includes('plain')) return 'text';
		if (contentType.includes('html')) return 'html';
		if (contentType.includes('json')) return 'json';
		if (contentType.includes('word') || contentType.includes('document')) return 'word';
		if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'excel';
		if (contentType.includes('powerpoint') || contentType.includes('presentation')) return 'powerpoint';
		if (contentType.includes('csv')) return 'csv';
		return 'other';
	};

	const loadPreview = async () => {
		if (!file || !file.id) {
			setError('No file selected');
			setLoading(false);
			return;
		}

		setLoading(true);
		setError(null);
		setPreviewUrl(null);
		setTextContent(null);
		
		try {
			console.log('Loading preview for file:', file.id, file.originalName);
			const response = await fileAPI.getFilePreview(file.id);
			console.log('Preview response received:', response);
			
			if (!response || !response.previewUrl) {
				throw new Error('Invalid preview response');
			}
			
			setPreviewUrl(response.previewUrl);

			// For text files, try to fetch and display content directly
			const fileType = getFileType();
			if (fileType === 'text' || fileType === 'json' || fileType === 'csv') {
				try {
					const textResponse = await fetch(response.previewUrl);
					if (!textResponse.ok) {
						throw new Error(`Failed to fetch text content: ${textResponse.status}`);
					}
					const text = await textResponse.text();
					setTextContent(text);
				} catch (textErr) {
					// If fetching text fails, fall back to iframe
					console.warn('Could not fetch text content directly:', textErr);
					// Don't set error here, just fall back to iframe
				}
			}
		} catch (err) {
			console.error('Error loading file preview:', err);
			setError(err.message || 'Failed to load file preview. Please check your permissions or try downloading the file.');
		} finally {
			setLoading(false);
		}
	};

	const renderPreview = () => {
		if (loading) {
			return (
				<div className="flex items-center justify-center h-96">
					<div className="text-center">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
						<p className="text-gray-600">Loading preview...</p>
					</div>
				</div>
			);
		}

		if (error) {
			return (
				<div className="flex items-center justify-center h-96">
					<div className="text-center">
						<svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
						</svg>
						<p className="text-red-600 mb-2">{error}</p>
						<button
							onClick={loadPreview}
							className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
						>
							Retry
						</button>
					</div>
				</div>
			);
		}

		if (!previewUrl) {
			return (
				<div className="flex items-center justify-center h-96">
					<p className="text-gray-500">No preview available</p>
				</div>
			);
		}

		const fileType = getFileType();

		switch (fileType) {
			case 'pdf':
				return (
					<iframe
						src={previewUrl}
						className="w-full h-full min-h-[600px] border-0"
						title="PDF Preview"
					/>
				);

			case 'image':
				return (
					<div className="flex items-center justify-center h-full bg-gray-100 p-4">
						<img
							src={previewUrl}
							alt={file.originalName}
							className="max-w-full max-h-[80vh] object-contain"
							onError={() => setError('Failed to load image')}
						/>
					</div>
				);

			case 'text':
			case 'html':
			case 'json':
			case 'csv':
				if (textContent !== null) {
					// Display text content directly
					return (
						<div className="w-full h-full min-h-[600px] border border-gray-300 rounded bg-white">
							<pre className="p-4 overflow-auto h-full text-sm font-mono whitespace-pre-wrap break-words">
								{textContent}
							</pre>
						</div>
					);
				}
				// Fallback to iframe if text content not available
				return (
					<iframe
						src={previewUrl}
						className="w-full h-full min-h-[600px] border border-gray-300 rounded"
						title="Text Preview"
					/>
				);

			case 'word':
			case 'excel':
			case 'powerpoint':
				// Try Microsoft Office Online Viewer
				// Note: This requires the file URL to be publicly accessible.
				// Since we are running on localhost, this might fail or require a specific setup.
				// However, per user request "should be load on in page", we attempt to render it.
				const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewUrl)}`;
				return (
					<div className="w-full h-full flex flex-col">
						<iframe
							src={officeViewerUrl}
							className="w-full h-full min-h-[600px] border-0 flex-1"
							title="Office Document Preview"
							onError={() => setError('Failed to load Office document preview. Please convert to PDF or download.')}
						/>
						<div className="bg-gray-50 px-4 py-2 border-t border-gray-200 text-center">
							<p className="text-sm text-gray-500 mb-2">If the document doesn't load, you can:</p>
							<div className="flex justify-center space-x-3">
								<button
									onClick={() => window.open(previewUrl, '_blank')}
									className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
								>
									Open Directly
								</button>
								<button
									onClick={() => {
										const link = document.createElement('a');
										link.href = previewUrl;
										link.download = file.originalName;
										link.click();
									}}
									className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
								>
									Download
								</button>
							</div>
						</div>
					</div>
				);

			default:
				// For other file types, show download option or use Google Docs Viewer as fallback
				const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(previewUrl)}&embedded=true`;
				return (
					<div className="flex flex-col items-center justify-center h-96 space-y-4">
						<svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
						</svg>
						<p className="text-gray-600">Preview not available for this file type</p>
						<div className="flex space-x-3">
							<button
								onClick={() => window.open(previewUrl, '_blank')}
								className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
							>
								Open in New Tab
							</button>
							<button
								onClick={() => {
									const link = document.createElement('a');
									link.href = previewUrl;
									link.download = file.originalName;
									link.click();
								}}
								className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
							>
								Download
							</button>
						</div>
						{/* Try Google Docs Viewer as fallback */}
						<div className="w-full mt-4">
							<p className="text-sm text-gray-500 mb-2">Trying alternative viewer...</p>
							<iframe
								src={googleViewerUrl}
								className="w-full h-96 border border-gray-300 rounded"
								title="Alternative Preview"
							/>
						</div>
					</div>
				);
		}
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
			{/* Backdrop */}
			<div
				className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
				onClick={onClose}
			></div>

			{/* Modal */}
			<div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
				<div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl">
					{/* Header */}
					<div className="bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
						<div className="flex items-center space-x-3">
							<div className="flex-shrink-0">
								<div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
									<svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
									</svg>
								</div>
							</div>
							<div>
								<h3 className="text-lg font-semibold text-gray-900">{file?.originalName || 'File Preview'}</h3>
								<p className="text-sm text-gray-500">
									{file?.contentType || 'Unknown type'} • {file?.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
								</p>
							</div>
						</div>
						<div className="flex items-center space-x-2">
							{previewUrl && (
								<button
									onClick={() => window.open(previewUrl, '_blank')}
									className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
									title="Open in new tab"
								>
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
									</svg>
								</button>
							)}
							<button
								onClick={onClose}
								className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
								title="Close"
							>
								<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>
					</div>

					{/* Preview Content */}
					<div className="bg-white px-6 py-4 max-h-[80vh] overflow-auto">
						{renderPreview()}
					</div>
				</div>
			</div>
		</div>
	);
};

export default FilePreviewModal;

